import { useEffect, useState } from "react";

import { useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { selectHideGizmo } from "features/outlineLaser";
import { hasMouseSupport } from "utils/misc";

import { selectLoadingHandles, selectPicker } from "../renderSlice";
import { Picker } from "../types";

export function useHandleCanvasCursor() {
    const {
        state: { canvas },
    } = useExplorerGlobals();
    const picker = useAppSelector(selectPicker);
    const loadingHandles = useAppSelector(selectLoadingHandles);
    const [usingSvgCursor, setUsingSvgCursor] = useState<"measure" | "cross" | "pointSnap" | "standard">("standard");
    const loading = loadingHandles.length !== 0;
    const hideLaserGizmo = useAppSelector(selectHideGizmo);

    useEffect(() => {
        if (!canvas) {
            return;
        }

        const measureCursorPickers = [
            Picker.CrossSection,
            Picker.Measurement,
            Picker.OrthoPlane,
            Picker.FollowPathObject,
            Picker.ClippingPlane,
            Picker.Area,
            Picker.Manhole,
            Picker.HeightProfileEntity,
            Picker.JiraIssuePosition,
            Picker.Dimension,
        ];

        const sectionCursorPickers = [Picker.SectionPlane];

        let newCursor: typeof usingSvgCursor = "standard";
        if ((picker === Picker.OutlineLaser && !hideLaserGizmo) || picker === Picker.Coordinates) {
            newCursor = "cross";
        } else if (picker === Picker.PointLine) {
            newCursor = "pointSnap";
        } else if (measureCursorPickers.includes(picker) || sectionCursorPickers.includes(picker)) {
            newCursor = "measure";
        }

        setUsingSvgCursor(newCursor);
    }, [picker, canvas, hideLaserGizmo]);

    useEffect(() => {
        if (!canvas) {
            return;
        }

        // When forceMobile is active, the mobile cursor overlay handles visual feedback.
        // Keep the native cursor visible so the user can still aim with the mouse.
        if (!hasMouseSupport()) {
            canvas.style.cursor = usingSvgCursor !== "standard" ? "crosshair" : "default";
            return;
        }

        if (usingSvgCursor !== "standard") {
            canvas.style.cursor = "none";
        } else if (loading) {
            canvas.style.cursor = "wait";
        } else {
            canvas.style.cursor = "default";
        }
    }, [canvas, usingSvgCursor, loading]);

    return usingSvgCursor;
}
